import axios, { type AxiosInstance } from 'axios';

/**
 * Per-request behaviour flags, attached via `config.meta`.
 *
 * `fallbackMessage` is where the `ApiBaseService.handleError('Failed to load X')` strings
 * from the Angular services live now. That service is NOT ported — its error handling
 * collapsed into the single response interceptor, which is what removes the double-toast bug
 * (today the interceptor AND handleError both fire).
 */
export interface RequestMeta {
  /** Suppress the automatic error toast — the caller renders the error inline. */
  silent?: boolean;
  /** Message shown when the server sends no message of its own. */
  fallbackMessage?: string;
  /** Suppress the 401 → logout side effect. Used by the auth endpoints themselves. */
  skipAuthRedirect?: boolean;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    meta?: RequestMeta;
  }
}

const COMMON = { timeout: 60_000 } as const;

/**
 * ============================================================================
 * THE BASE URL COMES FROM THE ENVIRONMENT. NOTHING IS HARDCODED HERE.
 * ============================================================================
 *
 * Set in `.env` (dev) and `.env.production` (prod). See `.env` for the rationale and
 * `docs/BACKEND-MAP.md` for the upstream routing.
 *
 * ONE BASE, down from four. `VITE_EXTRACT_BASE` and `VITE_ENRICH_BASE` went when document
 * extraction and address enrichment moved onto the main backend; `VITE_SUGGEST_BASE` went when the
 * two functions that used it turned out to have no callers and its hosts turned out to be down.
 *
 * Absolute values bypass the Vite dev proxy and talk to the backend directly. Relative values
 * resolve against the serving origin, which is what production uses so that nginx can proxy
 * them and the CSP's `connect-src 'self'` holds.
 *
 * `requireEnv` fails loudly at startup rather than letting a missing variable become a
 * request to `undefined/users/login`, which surfaces as a confusing 404 much later.
 */
function requireEnv(name: keyof ImportMetaEnv): string {
  // `import.meta.env` is indexed-access typed, so the read widens to `any`.
  const value: unknown = import.meta.env[name];
  if (typeof value !== 'string' || value === '') {
    throw new Error(
      `Missing required environment variable ${String(name)}. ` +
        `Add it to .env (see .env for the expected values).`,
    );
  }
  return value;
}

/**
 * The API origin + prefix, exported because one flow needs it OUTSIDE axios.
 *
 * Azure AD SSO starts with a full-page navigation to `{API_BASE}/auth/sso/login` — axios cannot make
 * that call, since the endpoint answers a redirect to Microsoft that the browser itself must follow.
 * Exporting the value rather than re-reading `import.meta.env` at the call site keeps the two from
 * drifting: the SSO redirect and every XHR then point at the same backend by construction.
 */
export const API_BASE = requireEnv('VITE_API_BASE');

export const api: AxiosInstance = axios.create({
  ...COMMON,
  baseURL: API_BASE,
});

/**
 * NO `suggestApi`. The AI name/address suggestion services at `192.168.51.27:6003` and `:6004` are
 * gone, and this instance existed only for them.
 *
 * ⚠️ IT WAS NEVER REACHED BY ANYTHING. Its only two consumers were `vendorApi.correctVendorName` and
 * `vendorApi.normalizeAddress`, and grepping `src/` finds no caller for either — so the documented
 * production 404 on `/api/v1/suggest-*` (nginx rewriting it into the main backend) was real but
 * academic: no screen ever fired the request. Both hosts now refuse the connection outright.
 */

/**
 * NO `extractApi`. Document extraction moved to `POST /api/v1/ai-extract` on the main backend, so it
 * goes through `api` and `VITE_EXTRACT_BASE` is gone — this instance was its only consumer. See
 * `vendorApi.extractDocument`.
 */

/**
 * NO `enrichApi`. AI address enrichment moved to `POST /api/v1/web-enrich` on the main backend, so it
 * goes through `api` and `VITE_ENRICH_BASE` is gone — this instance was its only consumer. See
 * `vendorApi.enrichAddress`.
 */

/**
 * Every axios instance the interceptors must be installed on. A one-element list today, kept as a
 * list because it is the seam that made the four-base era survivable — and because
 * `installInterceptors` iterating it is correct either way.
 */
export const ALL_CLIENTS: readonly AxiosInstance[] = [api];
